import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutTimeline, contactInfo } from '@/mocks/site';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSuccess(true);
      form.reset();
      return;
    }

    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || String(value).trim() === '') {
          body.append(key, String(value));
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9u5335ca23r0tr1aqpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const serverMsg = typeof parsed?.meta?.message === 'string'
        ? parsed.meta.message
        : typeof parsed?.message === 'string' ? parsed.message : '';

      if (res.ok && code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '众赢国际',
    url: 'https://ovelbrf.cn',
    description: '众赢国际是专业可靠的综合性金融服务平台，致力于为全球投资者提供安全、便捷、高效的投资交易服务。',
    foundingDate: '2016',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: contactInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      areaServed: 'CN',
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      streetAddress: contactInfo.address,
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于众赢国际',
    description: '了解众赢国际的品牌故事、发展历程、核心优势与联系方式。',
    url: 'https://ovelbrf.cn/about',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于众赢国际 - 品牌介绍、信誉与联系方式"
        description="了解众赢国际的品牌故事、发展历程、核心优势与联系方式。专业可靠的综合性金融服务平台，8年安全运营，服务超过50万用户。"
        keywords="众赢国际,关于众赢国际,众赢国际品牌,众赢国际联系方式,众赢国际信誉"
        canonicalUrl="https://ovelbrf.cn/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-4">
                  关于众赢国际
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  专业可靠的综合性金融服务平台
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                  众赢国际成立于2016年，是一家专注于为全球投资者提供专业金融服务的综合性平台。经过8年的稳健发展，我们已经服务了超过50万注册用户，涵盖股票、外汇、商品、指数等200多种交易品种。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                  我们始终坚持"以用户为中心"的经营理念，依托强大的技术研发能力和严格的风控体系，为用户提供安全、便捷、高效的一站式投资交易服务。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  平台采用银行级加密技术和多重安全防护机制，用户资金与平台运营资金完全隔离，并由合作银行进行资金托管，确保每一位用户的资产安全。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20financial%20office%20interior%20with%20warm%20golden%20lighting%2C%20professional%20corporate%20atmosphere%2C%20clean%20geometric%20lines%2C%20abstract%20architectural%20photography%2C%20elegant%20and%20sophisticated%20design&width=800&height=600&seq=about-brand-2024&orientation=landscape"
                  alt="众赢国际 - 专业金融服务平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-16 py-14 bg-background-100 rounded-2xl px-6 md:px-10">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">核心价值观</h2>
              <p className="text-sm text-foreground-500">众赢国际始终坚守的核心经营理念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-star-line', title: '安全第一', desc: '资金托管、银行级加密、多重风控，全方位保障用户资产安全' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为导向，持续优化产品体验和服务质量' },
                { icon: 'ri-lightbulb-flash-line', title: '技术创新', desc: '持续投入技术研发，保持平台技术领先优势' },
                { icon: 'ri-scales-line', title: '合规经营', desc: '严格遵守监管要求，坚持合法合规的经营原则' },
              ].map((val) => (
                <div key={val.title} className="text-center p-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className={`${val.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Development Timeline */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-3">
                发展历程
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">品牌成长之路</h2>
              <p className="text-sm text-foreground-500">从创立到壮大，众赢国际的每一步都坚实有力</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-8">
                {aboutTimeline.map((item, idx) => (
                  <div key={item.year} className={`flex flex-col md:flex-row items-start gap-4 md:gap-10 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 inline-block">
                        <span className="text-xs font-bold text-primary-600">{item.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-950 mt-1 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50 flex-shrink-0 relative z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-3">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">与我们取得联系</h2>
              <p className="text-sm text-foreground-500">如有任何疑问或合作意向，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="flex flex-col gap-6">
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">客服热线</h4>
                      <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">{contactInfo.email}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">微信公众号</h4>
                      <p className="text-sm text-foreground-500">{contactInfo.wechat}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">公司地址</h4>
                      <p className="text-sm text-foreground-500">{contactInfo.address}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">服务时间</h4>
                      <p className="text-sm text-foreground-500">{contactInfo.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">在线咨询</h3>
                {formSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="text-base font-semibold text-foreground-950 mb-2">提交成功！</p>
                    <p className="text-sm text-foreground-500">感谢您的咨询，我们将尽快与您联系。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="name" className="text-sm font-medium text-foreground-700">姓名</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-sm font-medium text-foreground-700">邮箱</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="phone" className="text-sm font-medium text-foreground-700">电话</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的联系电话（选填）"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="message" className="text-sm font-medium text-foreground-700">咨询内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请描述您的咨询内容（500字以内）"
                      ></textarea>
                    </div>

                    {/* Anti-spam honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="antibot-field"
                    />

                    {formError && (
                      <p className="text-sm text-accent-600 bg-accent-50 px-4 py-2.5 rounded-lg">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? '提交中...' : '提交咨询'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}